<template>
  <ExternalQueryList
      endpoint="/api/v2/partnerships"
      :headlines="headlines"
      :filters="filters"
      :can-update="() => true"
      @show="partnership => $router.push('/companies/'+partnership.attributes.company_id)"
  >
    <template v-slot:default="{ row: partnership }">
      <td class="table__ellipsis">
        <router-link :to="'/companies/'+partnership.attributes.company_id">
          {{ partnership.attributes.company_name }}
        </router-link>
      </td>

      <td class="table__ellipsis">
        <router-link :to="'/portals/' + partnership.attributes.portal_id">
          {{ partnership.attributes.portal_name }}
        </router-link>
      </td>

      <td class="table__ellipsis">
        <Avatar :color="partnership.attributes.portal_company_user_color">
          {{ partnership.attributes.portal_company_user_initials }}
        </Avatar>
      </td>

      <td class="table__ellipsis">
        {{ partnership.attributes.price | price }}
      </td>

      <td class="table__ellipsis">
        {{ partnership.attributes.recent_content_count }}
      </td>

      <td class="table__ellipsis">
        {{ partnership.attributes.latest_content_at | date }}
      </td>

      <td class="table__ellipsis">
        <span v-if="partnership.attributes.latest_content_status === 'green'" class="label label-success">
          Green
        </span>

        <span v-if="partnership.attributes.latest_content_status === 'amber'" class="label label-warning">
          Amber
        </span>

        <span v-if="partnership.attributes.latest_content_status === 'red'" class="label label-danger">
          Red
        </span>
      </td>

      <td class="table__ellipsis">
        <StatusLabel :status="partnership.attributes.status"/>
      </td>
    </template>

    <template v-slot:list-actions="{ filterValues, canUpdateSelected, load, selected }">
      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#update-portal-user-modal" data-toggle="modal">
          <i class="fa fa-user"></i> Update Portal User
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#" @click.prevent="destroyPortalCompanyUsers(selected ,load)">
          <i class="fa fa-times"></i> Delete Portal User
        </a>
      </li>

      <li v-if="hasPermission('admin')">
        <a href="#" @click.prevent="download(filterValues)">
          <i class="fa fa-download"/> Download
        </a>
      </li>
    </template>

    <template v-slot:modals="{selected, load}">
      <UpdatePortalUserModal
          :selected="selected"
          :users="users"
          @updated="load"
      />
    </template>
  </ExternalQueryList>
</template>

<script>
import ExternalQueryList from '../../../Panels/Lists/ExternalQuery'
import StatusLabel from '../../../StatusLabel'
import axios from "axios";
import download from "downloadjs";
import store from '@/store'
import Permissions from "../../../../mixins/Permissions";
import Avatar from "../../../Avatar.vue";
import UpdatePortalUserModal from "../../../Modals/Relations/Partnerships/UpdatePortalUserModal.vue";
import http from "@/http";
import modal from "@/modal";
import notify from "@/notify";

export default {
  components: {  UpdatePortalUserModal, Avatar, ExternalQueryList, StatusLabel},

  mixins: [Permissions],

  props: {
    users: {
      required: true
    },

    portals: {
      required: true
    }
  },

  data() {
    return {
      shared: store
    }
  },

  computed: {
    headlines() {
      return [
        {width: 5, text: 'Subscriber'},
        {width: 2, text: 'Portal'},
        {width: 2, text: 'User'},
        {width: 2, text: 'Price'},
        {width: 2, text: 'Recent Content', sort_column: 'recent_content_count', description:'Articles, events, files and jobs published within the last 3 months.'},
        {width: 2, text: 'Latest Content', sort_column: 'latest_content_at'},
        {width: 3, text: 'Latest Content Status'},
        {width: 2, text: 'Status'},
      ]
    },

    filters() {
      return [
        [
          {
            width: 10,
            widget: 'text',
            name: 'search',
            placeholder: 'Search...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'has_automatic_renewal',
            options: [
              {value: 0, label: 'Without Automatic Renewal'},
              {value: 1, label: 'With Automatic Renewal'}
            ],
            nullable: true,
            placeholder: 'Choose Automatic Renewal...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'status',
            options: [
              {value: 'active', label: 'Active'},
              {value: 'expired', label: 'Expired'}
            ],
            nullable: true,
            placeholder: 'Choose Status...'
          },
          {
            width: 4,
            widget: 'button',
            class: 'btn-white',
            text: '<i class="fa fa-times"></i> Clear',
            event: 'clear'
          }
        ],
        [
          {
            width: 5,
            widget: 'date',
            name: 'ends_from',
            placeholder: 'Ends from...'
          },
          {
            width: 5,
            widget: 'date',
            name: 'ends_to',
            placeholder: 'Ends to...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'portal_id',
            options: this.portals.map(portal => ({value: portal.id, label: portal.attributes.name})),
            nullable: true,
            placeholder: 'Choose Portal...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'portal_company_user_id',
            options: this.users.map(user => ({value: user.id, label: user.attributes.full_name})),
            nullable: true,
            placeholder: 'Choose User...'
          },
          {
            width: 4,
            widget: 'submit'
          }
        ]
      ]
    }
  },

  methods: {
    download(filterValues) {
      axios.post('/api/v1/reports/partnerships', {}, {
        headers: {
          'Authorization': 'Bearer ' + this.shared.token
        },
        params: Object.entries(filterValues).filter(entry => entry[1]).reduce((result, entry) => {
          result['filter[' + entry[0] + ']'] = entry[1]

          return result
        }, {}),
        responseType: 'blob'
      })
          .then(response => {
            download(response.data, response.headers['content-disposition'].slice(21, -1))
          })
    },

    destroyPortalCompanyUsers(selected, load) {
      modal.confirm('Are your sure that you want to remove the users?', () => {
        Promise
            .all(selected.map(partnership => http.delete('/api/v2/companies/' + partnership.attributes.company_id + '/portals/' + partnership.attributes.portal_id + '/user').catch(() => {
            })))
            .then(() => {
              notify.success('The portal users were deleted')

              load()
            })

      })
    }
  }
}
</script>
